import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Box, Tabs, Tab } from '@mui/material'
import { SearchHeader } from '../common'
import { TruckSelectorDialog } from '../dialogs'
import { DashboardCardV2 } from '../cards'
import { useLogistics } from '@/hooks/logistics/useLogistics'
import { useClientUsers } from 'hooks/useUsers'
import TransportTrucksTab from './TransportTrucksTab'
import TransportMapTab from './TransportMapTab'
import TransportTransportersTab from './TransportTransportersTab'
import TransportInstallationNeedsTab from './TransportInstallationNeedsTab'
import { ShipmentPlan } from '@otw/models/logistics'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from 'hooks/inventory/useRepairShopLocations'
import LegPhotosDialog from '../dialogs/LegPhotosDialog'
import ShipmentPlanDialog from '../dialogs/ShipmentPlanDialog'
import { useLocation, useNavigate } from 'react-router-dom'
import type {
  DraftTruckItem,
  PhotoApprovalItem,
  MissingReadyDateItem,
} from '../cards/DashboardCardV2'
import { getTruckStatus } from '@otw/models/logistics'

const OPTIC_FILTER_VALUE = 'OPTIC'
const OPTIC_FILTER_LABEL = 'Optic truck works'
const ALL_STATUS_FILTER_VALUE = 'ALL'

const TransportTab: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(location.search)
    return params.get('transportVin') ?? ''
  })
  const [customerFilter, setCustomerFilter] = useState<string>('ALL')
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUS_FILTER_VALUE)
  const [isActiveFilter, setIsActiveFilter] = useState(true)
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  const [activeTransportSubTab, setActiveTransportSubTab] = useState(0)
  const [isTruckSelectorDialogOpen, setIsTruckSelectorDialogOpen] = useState(false)
  const [isShipmentPlanDialogOpen, setIsShipmentPlanDialogOpen] = useState(false)
  const [planToEdit, setPlanToEdit] = useState<ShipmentPlan | null>(null)
  const [insertRepairAfterLegIndex, setInsertRepairAfterLegIndex] = useState<number | null>(null)
  const [dialogTruckWithVehicle, setDialogTruckWithVehicle] = useState<PhotoApprovalItem['truckWithVehicle'] | null>(null)
  const [focusedTruckId, setFocusedTruckId] = useState<string | null>(() => {
    const params = new URLSearchParams(location.search)
    return params.get('transportTruckId')
  })
  const openedPlanFromSearchJumpRef = useRef(false)
  const [dashboardLegPhotosState, setDashboardLegPhotosState] = useState<{
    planId: string
    plan: ShipmentPlan
    truckWithVehicle: PhotoApprovalItem['truckWithVehicle']
    legItems: {
      fromLabel: string
      toLabel: string
      company_id?: string
      to_type?: 'pickup' | 'repair'
      latestStatusNotes?: string
    }[]
    initialLegIndex: number
    vehicle_damage?: ShipmentPlan['vehicle_damage'] | null
  } | null>(null)
  const { transportTrucksWithVehicles, approveLegPhotos } = useLogistics()
  const { clientUsers } = useClientUsers()
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const shouldOpenLegPhotos =
      (params.get('openLegPhotos') === '1' || params.get('openPhotos') === '1') &&
      Boolean(params.get('vehicleId'))

    if (shouldOpenLegPhotos) {
      setActiveTransportSubTab(1)
    }
  }, [location.search])

  const clearTransportJumpParams = React.useCallback(() => {
    const params = new URLSearchParams(location.search)
    const hadJumpParams =
      params.has('transportTruckId') ||
      params.has('transportVin') ||
      params.has('shipmentPlanId')
    if (!hadJumpParams) return
    params.delete('transportTruckId')
    params.delete('transportVin')
    params.delete('shipmentPlanId')
    const nextSearch = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    )
  }, [location.pathname, location.search, navigate])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const transportTruckId = params.get('transportTruckId')
    if (!transportTruckId) return
    setActiveTransportSubTab(0)
    setFocusedTruckId(transportTruckId)
  }, [location.search])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const shipmentPlanId = params.get('shipmentPlanId')
    const transportTruckId = params.get('transportTruckId')
    if (!shipmentPlanId || !transportTruckId) return

    const truckWithVehicle = transportTrucksWithVehicles.find(
      (entry) => entry.truck.id === transportTruckId,
    )
    if (!truckWithVehicle) return

    const plan = truckWithVehicle.shipmentPlans.find((entry) => entry.id === shipmentPlanId)
    if (!plan) return

    setActiveTransportSubTab(0)
    setFocusedTruckId(transportTruckId)
    setDialogTruckWithVehicle(truckWithVehicle)
    setPlanToEdit(plan)
    setInsertRepairAfterLegIndex(null)
    setIsShipmentPlanDialogOpen(true)
    openedPlanFromSearchJumpRef.current = true

    params.delete('shipmentPlanId')
    params.delete('transportTruckId')
    params.delete('transportVin')
    const nextSearch = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true },
    )
  }, [location.pathname, location.search, navigate, transportTrucksWithVehicles])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const transportVin = params.get('transportVin')
    if (!transportVin) return
    setActiveTransportSubTab(0)
    setSearchTerm(transportVin)
    setCustomerFilter('ALL')
    setStatusFilter(ALL_STATUS_FILTER_VALUE)
    setIsActiveFilter(true)
    setFlaggedOnly(false)
  }, [location.search])

  const getLocationLabel = (locId: string, locType: 'pickup' | 'repair') => {
    if (locType === 'repair') {
      const found = repairShopLocations?.find((r) => r.id === locId)
      if (!found) return 'Unknown'
      return found.name || found.contact_name || 'Unknown Shop'
    }
    const found = pickupLocations?.find((p) => p.id === locId)
    if (!found) return 'Unknown'
    return found.contact_name || 'Unknown Contact'
  }

  const handleTruckJump = (truckId: string) => {
    setActiveTransportSubTab(0)
    setFocusedTruckId(truckId)
  }

  const handleSearchChange = (term: string) => {
    setSearchTerm(term)
    // If user manually edits search, clear one-time transport jump params.
    clearTransportJumpParams()
  }

  const shouldRenderDashboardCards = activeTransportSubTab === 0

  const handleDraftTruckClick = (item: DraftTruckItem) => {
    handleTruckJump(item.truckWithVehicle.truck.id)
  }

  const handleMissingReadyDateClick = (item: MissingReadyDateItem) => {
    handleTruckJump(item.truckWithVehicle.truck.id)
  }

  const handlePhotoApprovalClick = (item: PhotoApprovalItem) => {
    const legItems = (item.plan.shipment_legs ?? []).map((leg) => {
      const hist = leg.status_history
      const latestNotes = hist?.length ? hist[hist.length - 1]?.notes : undefined
      return {
        fromLabel: getLocationLabel(leg.from_id, leg.from_type),
        toLabel: getLocationLabel(leg.to_id, leg.to_type),
        company_id: leg.company_id,
        to_type: leg.to_type,
        latestStatusNotes: latestNotes,
      }
    })

    setDashboardLegPhotosState({
      planId: item.plan.id,
      plan: item.plan,
      truckWithVehicle: item.truckWithVehicle,
      legItems,
      initialLegIndex: item.legIndex,
      vehicle_damage: item.plan.vehicle_damage ?? undefined,
    })
  }

  const handleDashboardApprovePreDelivery = async (_planId: string, legIndex: number) => {
    const plan = dashboardLegPhotosState?.plan
    if (!plan) return
    await approveLegPhotos({ plan, legIndex })
  }

  const handleDashboardRejectPreDelivery = (_planId: string, legIndex: number) => {
    if (!dashboardLegPhotosState) return
    setDashboardLegPhotosState(null)
    setDialogTruckWithVehicle(dashboardLegPhotosState.truckWithVehicle)
    setPlanToEdit(dashboardLegPhotosState.plan)
    setInsertRepairAfterLegIndex(legIndex)
    setIsShipmentPlanDialogOpen(true)
  }

  const handleCloseShipmentPlanDialog = () => {
    setIsShipmentPlanDialogOpen(false)
    setPlanToEdit(null)
    setInsertRepairAfterLegIndex(null)
    setDialogTruckWithVehicle(null)
    setFocusedTruckId(null)
    if (openedPlanFromSearchJumpRef.current) {
      setSearchTerm('')
      openedPlanFromSearchJumpRef.current = false
    }
    clearTransportJumpParams()
  }

  // Placeholder customer options - will be populated when data is available
  const customerFilterOptions = useMemo(
    () => {
      const counts = new Map<string, number>()
      const clientLabelById = new Map<string, string>()
      let opticCount = 0

      clientUsers.forEach((client) => {
        const fullName = `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim()
        clientLabelById.set(client.id, fullName || client.email || client.id)
      })

      transportTrucksWithVehicles.forEach(({ shipmentPlans }) => {
        shipmentPlans.forEach((plan) => {
          const clientId = plan.client_user_id
          if (!clientId || clientId === 'optic') {
            opticCount += 1
            return
          }
          counts.set(clientId, (counts.get(clientId) ?? 0) + 1)
        })
      })

      const sortedIds = [...counts.keys()].sort((a, b) => {
        const labelA = clientLabelById.get(a) ?? a
        const labelB = clientLabelById.get(b) ?? b
        return labelA.localeCompare(labelB)
      })

      return [
        { value: 'ALL', label: 'All' },
        { value: OPTIC_FILTER_VALUE, label: OPTIC_FILTER_LABEL, count: opticCount },
        ...sortedIds.map((id) => ({
          value: id,
          label: clientLabelById.get(id) ?? id,
          count: counts.get(id) ?? 0,
        })),
      ]
    },
    [transportTrucksWithVehicles, clientUsers]
  )

  const statusFilterOptions = useMemo(() => {
    const statusCounts = new Map<string, number>()

    transportTrucksWithVehicles.forEach(({ truck }) => {
      const status = getTruckStatus(truck)
      statusCounts.set(status, (statusCounts.get(status) ?? 0) + 1)
    })

    const statusLabelByValue: Record<string, string> = {
      draft: 'Draft',
      assigned: 'Assigned',
      awaiting_transport: 'Awaiting transport',
      in_transit: 'In transit',
      repair: 'Repair',
    }

    const orderedStatuses = ['draft', 'assigned', 'awaiting_transport', 'in_transit', 'repair']

    return [
      { value: ALL_STATUS_FILTER_VALUE, label: 'All statuses' },
      ...orderedStatuses.map((status) => ({
        value: status,
        label: statusLabelByValue[status] || status,
        count: statusCounts.get(status) ?? 0,
      })),
    ]
  }, [transportTrucksWithVehicles])

  return (
    <>
      <Box sx={{ px: 2 }}>
        <Tabs
          value={activeTransportSubTab}
          onChange={(_, v) => {
            setActiveTransportSubTab(v)
            // Clear one-time transport jump params when switching subtabs.
            clearTransportJumpParams()
          }}
          textColor="primary"
          sx={{
            mt: 1,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            '& .MuiTabs-flexContainer': {
              gap: 0.5,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              minHeight: 40,
              color: 'text.primary',
              px: 2,
              borderRadius: 1.5,
              '&.Mui-selected': {
                color: 'primary.contrastText',
                backgroundColor: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.main',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Trucks" />
          <Tab label="Transporters" />
          <Tab label="Installation needs" />
          <Tab label="Map" />
        </Tabs>
      </Box>

      {activeTransportSubTab !== 2 ? (
        <Box sx={{ px: 2, mt: 2 }}>
          <SearchHeader
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search..."
            showSearch={true}
            showSoldToUserFilter={true}
            soldToUserFilter={customerFilter}
            onSoldToUserFilterChange={setCustomerFilter}
            soldToUserOptions={customerFilterOptions}
            showStatusFilter={true}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            statusFilterOptions={statusFilterOptions}
            showActiveCompletedToggle={activeTransportSubTab === 0}
            isActiveFilter={isActiveFilter}
            onActiveCompletedToggle={(nextIsActive) => {
              setIsActiveFilter(nextIsActive)
              setStatusFilter(ALL_STATUS_FILTER_VALUE)
            }}
            showFlaggedCheckbox={true}
            flaggedOnly={flaggedOnly}
            onFlaggedOnlyChange={setFlaggedOnly}
            showCreateButton={activeTransportSubTab === 0}
            createButtonText="Add truck"
            onCreateClick={() => setIsTruckSelectorDialogOpen(true)}
            customDashboardContent={
              shouldRenderDashboardCards ? (
                <DashboardCardV2
                  transportTrucksWithVehicles={transportTrucksWithVehicles}
                  onDraftTruckClick={handleDraftTruckClick}
                  onPhotoApprovalClick={handlePhotoApprovalClick}
                  onMissingReadyDateClick={handleMissingReadyDateClick}
                />
              ) : null
            }
          />
        </Box>
      ) : null}

      {activeTransportSubTab === 0 && (
        <TransportTrucksTab
          searchTerm={searchTerm}
          customerFilter={customerFilter}
          statusFilter={statusFilter}
          isActiveFilter={isActiveFilter}
          flaggedOnly={flaggedOnly}
          focusTruckId={focusedTruckId}
          onFocusedTruckHandled={() => setFocusedTruckId(null)}
        />
      )}
      {activeTransportSubTab === 1 && (
        <TransportTransportersTab
          searchTerm={searchTerm}
          customerFilter={customerFilter}
          statusFilter={statusFilter}
          flaggedOnly={flaggedOnly}
        />
      )}
      {activeTransportSubTab === 2 && (
        <TransportInstallationNeedsTab
          transportTrucksWithVehicles={transportTrucksWithVehicles}
        />
      )}
      {activeTransportSubTab === 3 && (
        <TransportMapTab searchTerm={searchTerm} customerFilter={customerFilter} statusFilter={statusFilter} />
      )}

      <TruckSelectorDialog
        open={isTruckSelectorDialogOpen}
        onClose={() => setIsTruckSelectorDialogOpen(false)}
      />
      <LegPhotosDialog
        open={!!dashboardLegPhotosState}
        onClose={() => setDashboardLegPhotosState(null)}
        planId={dashboardLegPhotosState?.planId ?? ''}
        legItems={dashboardLegPhotosState?.legItems ?? []}
        initialLegIndex={dashboardLegPhotosState?.initialLegIndex}
        vehicle_damage={dashboardLegPhotosState?.vehicle_damage ?? undefined}
        onApprovePreDelivery={handleDashboardApprovePreDelivery}
        onRejectPreDelivery={handleDashboardRejectPreDelivery}
      />
      <ShipmentPlanDialog
        open={isShipmentPlanDialogOpen}
        onClose={handleCloseShipmentPlanDialog}
        truckWithVehicle={dialogTruckWithVehicle}
        planToEdit={planToEdit}
        insertRepairAfterLegIndex={insertRepairAfterLegIndex}
      />
    </>
  )
}

export default TransportTab
